import type { ReactNode } from "react";
import { Link } from "react-router";
import type { ChannelId } from "../../../shared/goals/channels";
import { CHANNEL_ORDER, CHANNELS } from "../../../shared/goals/channels";
import type { GoalConfig } from "../../../shared/goals/config";
import { splitVariant, type Plan } from "../../../shared/goals/plan";
import { READINESS } from "../../../shared/goals/readiness";
import { Icon, type IconName } from "../../components/ui/Icon";
import { Info } from "../../components/ui/Info";
import { Orb } from "../../components/ui/Orb";
import { Rich } from "../../components/ui/Rich";
import { cap, dayMonth, fmt, longDay, planDay } from "./text";

/**
 * Step 5, the review: the answer first, then what it will do, the money over time, what it decided and where
 * it disagrees with the owner's rules, and what comes next. Everything comes from the plan and the goal.
 */

// --- What the plan says about each channel -------------------------------------

export const metaTests = (p: Plan) => p.tests.filter((t) => /meta/i.test(t.name));
/** Plans word the ads' start differently; the start that needs the Meta account and the ads approved is the ads going live. */
export const adsStart = (p: Plan) => p.starts.find((s) => ["meta_account", "ad_creative_approval"].every((n) => s.needs.includes(n))) ?? p.starts.find((s) => /\blive\b/i.test(s.what)) ?? null;
export const mailStart = (p: Plan) => p.starts.find((s) => s.needs.some((n) => n.startsWith("email_"))) ?? p.starts.find((s) => /cold email/i.test(s.what)) ?? null;
const ideaOf = (v: string): [string, string] => {
  const { key, text } = splitVariant(v);
  return text ? [key, cap(text)] : [v, ""];
};

const READY_ICON: Record<string, IconName> = {
  meta_account: "bolt",
  meta_pixel: "target",
  email_mailbox: "mail",
  email_warmup: "mail",
  email_copy_approval: "check",
  ad_creative_approval: "film",
  linkedin_account: "users",
};

function Answer({ plan, target }: { plan: Plan; target: number }) {
  const lever = String(plan.budget.reachable).match(/What would change it:\s*([^.]+)\./i);
  return (
    <section className="card psec rv-ans" id="p-answer">
      <div className="eyebrow">The answer</div>
      <h2 className="rv-big h-i">
        Likely {plan.headline.expected} of {target.toLocaleString("en-US")}
        <Info label="Can it be done?">
          <Rich text={plan.budget.reachable} />
        </Info>
      </h2>
      <p className="rv-line">
        Needs {plan.headline.pace.replace(/^About /, "about ")} · expects {plan.headline.cost} each
      </p>
      <div className="rv-lever">
        <span className="kind" style={{ color: "var(--fire-2)" }}>
          <Icon name="target" />
          What gets it to {target.toLocaleString("en-US")}
        </span>
        <p>{lever ? `${cap(lever[1].trim())}.` : plan.bet}</p>
      </div>
    </section>
  );
}

function Channel({ id, plan, today }: { id: ChannelId; plan: Plan; today: Date }) {
  const icon: IconName = id === "meta" ? "bolt" : "mail";
  const start = id === "meta" ? adsStart(plan) : mailStart(plan);
  const when = planDay(start?.when, today);
  const test = id === "meta" ? metaTests(plan)[0] : undefined;
  let body;
  if (test) {
    const ideas = test.variants.map(ideaOf);
    body = (
      <>
        <p>
          Tests {ideas.length} video ideas for {test.days} days{when ? ` from ${longDay(when)}` : ""}. Then the money follows the ideas bringing results cheapest.
        </p>
        <ul className="rv-ideas">
          {ideas.map(([name, more], i) => (
            <li key={i}>
              <b>{name}</b>
              {more && <span>{more}</span>}
            </li>
          ))}
        </ul>
      </>
    );
  } else if (start) {
    body = <p>{`${cap(start.what.replace(/\.$/, ""))}${when ? `, from ${longDay(when)}` : ""}.`}</p>;
  } else return null;
  return (
    <div className="rv-ch">
      <span className="ki">
        <Icon name={icon} />
      </span>
      <div className="rv-cb">
        <h4>{CHANNELS[id].label}</h4>
        {body}
      </div>
    </div>
  );
}

function Money({ plan, config, today }: { plan: Plan; config: GoalConfig; today: Date }) {
  const split = plan.budget.split;
  const tot = split.reduce((a, b) => a + b.usd, 0);
  const ads = split.filter((b) => /\bads\b/i.test(b.item));
  const ad = ads[0] ?? split[0];
  const adTot = ads.length ? ads.reduce((a, b) => a + b.usd, 0) : (ad?.usd ?? 0);
  const perDay = ad ? (ad.item.match(/x \$(\d+)/) ?? ad.item.match(/\$(\d+) a day/))?.[1] : undefined;
  const t0 = planDay(plan.timeline[0]?.when, today) ?? today;
  const end = planDay(plan.goal.deadline, today) ?? planDay(config.result.by, today) ?? today;
  const liveStart = adsStart(plan);
  const live = planDay(liveStart?.when, today);
  const test = metaTests(plan)[0];
  const called = live && test ? new Date(live.getTime() + test.days * 864e5) : null;
  const span = end.getTime() - t0.getTime();
  const pos = (d: Date) => (span > 0 ? Math.max(0, Math.min(100, ((d.getTime() - t0.getTime()) / span) * 100)) : 0);
  const seg = (a: Date | null, b: Date | null, cls: string) => (a && b ? <i className={cls} style={{ left: `${pos(a)}%`, width: `${pos(b) - pos(a)}%` }} /> : null);
  const mark = (d: Date | null, label: string, cls = "") =>
    d ? (
      <span className={`rv-mk ${cls}`} style={{ left: `${pos(d)}%` }}>
        <b>{label}</b>
        <em>{dayMonth(d)}</em>
      </span>
    ) : null;
  return (
    <section className="card psec" id="p-money">
      <div className="psec-h">
        <div>
          <h3 className="h-i">
            Money and time
            <Info label="Where the money goes">
              <ul className="rv-split">
                {split.map((b, i) => (
                  <li key={i}>
                    <span>{b.item}</span>
                    <b>${fmt(b.usd)}</b>
                  </li>
                ))}
              </ul>
            </Info>
          </h3>
        </div>
      </div>
      <div className="rv-bar">
        <div className="rv-track">
          {seg(t0, live, "rv-set")}
          {seg(live, called, "rv-test")}
          {seg(called, end, "rv-scale")}
        </div>
        <div className="rv-mks">
          {mark(live, "Ads live")}
          {mark(called, "Test decided")}
          {mark(end, "Deadline", "end")}
        </div>
      </div>
      <div className="rv-keys">
        <span>
          <i className="rv-set" />
          Set up
        </span>
        <span>
          <i className="rv-test" />
          Testing{test ? `, ${test.days} days` : ""}
        </span>
        <span>
          <i className="rv-scale" />
          Money follows what works
        </span>
      </div>
      <p className="rv-line">
        ${fmt(adTot)} on ads{perDay ? `, $${perDay} a day` : ""} · ${fmt(tot - adTot)} for tools, data and AI
      </p>
    </section>
  );
}

interface Props {
  /** Where "The full plan" opens. */
  planHref: string;
  plan: Plan;
  config: GoalConfig;
  version: number;
  approved: boolean;
  /** What the plan decided, with the owner's "Not right" (PlanCalls). */
  calls: ReactNode;
  /** The setup changed after this plan was made, so it cannot be approved as it is. */
  stale: boolean;
  onPlanAgain: () => void;
  planBusy: boolean;
  onHow: () => void;
}

export function Review({ planHref, plan, config, version, approved, calls, stale, onPlanAgain, planBusy, onHow }: Props) {
  const today = new Date();
  const on = CHANNEL_ORDER.filter((k) => config.channels[k]?.on);
  const needs = [...new Set(plan.starts.flatMap((s) => s.needs))];
  const ideas = metaTests(plan)[0]?.variants.length ?? 0;
  const live = planDay(adsStart(plan)?.when, today);
  const by = live ? new Date(live.getTime() - 864e5) : null;

  return (
    <>
      <div className="rv-links">
        <Link className="chip link" to={planHref}>
          <Icon name="doc" />
          The full plan
          <Icon name="right" />
        </Link>
        <button type="button" className="chip link" onClick={onHow}>
          <Icon name="tool" />
          How it was made
        </button>
        <span className="rvst">
          Version {version} · {approved ? "approved by you" : "waiting for you"}
        </span>
      </div>
      {approved && (
        <section className="card psec" id="p-ready" style={{ borderColor: "var(--fire-line)" }}>
          <div className="clear">
            <Orb live />
            <span>
              <b>Approved.</b> Next is getting ready: the connections, the ads and the emails.
            </span>
          </div>
        </section>
      )}
      {!approved && stale && (
        <section className="card psec" id="p-stale" style={{ borderColor: "var(--warn-line)" }}>
          <div className="clear">
            <Icon name="info" />
            <span>Your settings changed after this plan was made. Plan again to include them.</span>
            <button type="button" className="btn sm" disabled={planBusy} onClick={onPlanAgain}>
              Plan again
            </button>
          </div>
        </section>
      )}
      <Answer plan={plan} target={config.result.target} />
      <section className="card psec" id="p-do">
        <div className="psec-h">
          <div>
            <h3>What it will do</h3>
          </div>
        </div>
        <div className="rv-chs">
          {on.map((k) => (
            <Channel key={k} id={k} plan={plan} today={today} />
          ))}
        </div>
      </section>
      <Money plan={plan} config={config} today={today} />
      {calls}
      {(needs.length > 0 || ideas > 0) && (
        <section className="card psec rv-next" id="p-next">
          <div className="psec-h">
            <div>
              <h3>After you approve</h3>
              <div className="sub">On the goal's Get ready page</div>
            </div>
          </div>
          <ul className="rv-todo">
            {ideas > 0 && (
              <li>
                <Icon name="film" />
                <span>
                  Add the {ideas} ad videos{by ? ` by ${longDay(by)}` : ""}
                </span>
              </li>
            )}
            {needs
              .filter((n) => !(ideas > 0 && n === "ad_creative_approval"))
              .map((n) => (
                <li key={n}>
                  <Icon name={READY_ICON[n] ?? "check"} />
                  <span>{READINESS[n]?.label ?? n}</span>
                </li>
              ))}
          </ul>
        </section>
      )}
    </>
  );
}
